<template>
  <Transition name="fade">
    <div id="alert-banner" v-if="banner?.name" class="w-full z-40 sticky">
      <Alert class="rounded-none" :onClose="close" :tone="banner.tone" :dismissable="banner.dismissible != false" hideIcon>
        <span class="block" v-html="banner.html"></span>
      </Alert>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { Alert } from "prism";
import Rails from "@rails/ujs";
import Routes from "@/modules/routes";
import Configs from "@/modules/configs";
import { onMounted, onUnmounted, ref, nextTick, watch } from "vue";
import { useAlertBanner } from "@/composables/alert_banner";

interface AlertBannerConfig {
  name: string;
  slug: string;
  html: string;
  tone?: string;
  remember_cta_action?: boolean;
  dismissible?: boolean;
}

const banner = ref(null);
const { setVisible, setHeight, isDismissed, markDismissed } = useAlertBanner();

onMounted(() => {
  const bannerConfig = Configs.get("banner");
  const config = bannerConfig && typeof bannerConfig === "object" ? (bannerConfig as AlertBannerConfig) : null;

  if (config) {
    const identifier = config.name.includes("promo_banner") ? config.name : config.slug;
    banner.value = isDismissed(identifier) ? null : config;
  } else {
    banner.value = null;
  }

  updateBannerState();
});

let bannerResizeObserver: ResizeObserver | null = null;

const stopObservingBannerHeight = () => {
  bannerResizeObserver?.disconnect();
  bannerResizeObserver = null;
};

const observeBannerHeight = (bannerElement: HTMLElement) => {
  stopObservingBannerHeight();
  bannerResizeObserver = new ResizeObserver(() => {
    setHeight(bannerElement.clientHeight || 0);
  });
  bannerResizeObserver.observe(bannerElement);
};

const updateBannerState = () => {
  const isVisible = !!banner.value?.name;

  if (isVisible) {
    nextTick(() => {
      const bannerElement = document.getElementById("alert-banner");
      const height = bannerElement?.clientHeight || 0;
      setHeight(height);

      if (bannerElement) {
        observeBannerHeight(bannerElement);
      }
    });
  } else {
    stopObservingBannerHeight();
    setHeight(0);
  }

  setVisible(isVisible);
};

watch(banner, updateBannerState, { deep: true });

onUnmounted(() => {
  stopObservingBannerHeight();
});

const close = async () => {
  if (banner.value.remember_cta_action) {
    const bannerIdentifier = banner.value.name.includes("promo_banner") ? banner.value.name : banner.value.slug;
    const key = banner.value.name.includes("promo_banner")
      ? "promo_banner_dismissed"
      : `${banner.value.name}_dismissed`;

    // Keeping this locally to prevent the banner from reappearing on navigation
    markDismissed(bannerIdentifier);

    await fetch(Routes.get("session_preferences_url"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": Rails.csrfToken(),
      },
      body: JSON.stringify({
        key: key,
        value: {
          [bannerIdentifier]: true,
        },
      }),
    });
  }

  banner.value = null;
  setVisible(false);
  setHeight(0);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
  transition-delay: 0s;
}
.fade-enter-from,
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

#alert-banner {
  font-size: 16px;
}
</style>
